/*
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  CardPartnersDirectory,
  CardPartnersDirectoryProps,
  CardPartnersDirectoryView,
  ListScrollMode,
  type RoutingAdapter,
  useCardPartnersRouting,
  useCategoriesQuery,
} from "@farmlands/card-partners-directory";
import { useIsMobile } from "@farmlands/shadcn-ui";
import { useTheme } from "@mui/material";
import { type FC, type JSX, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { PageContainer } from "../../components/layout/PageContainer";
import { useThemeBackgroundColor } from "../../hooks/useThemeBackgroundColor";
import TestIds from "../../testIds";
import { APPBAR_HEIGHT } from "../../util/consts";

import {
  branchesApi,
  cardPartnersDirectoryWebUrl,
  categoriesApiConfig,
  mapConfig,
  MOBILE_LIST_STICKY_TOP_OFFSET,
  partnerAssets,
} from "./consts";

import "./CardPartnerDirectoryPage.scss";

const useReactRouterRoutingAdapter: () => RoutingAdapter = () => {
  const [, setSearchParams] = useSearchParams();
  return useMemo<RoutingAdapter>(
    () => ({
      readSearch: () => window.location.search,
      writeSearch: (search, { replace }) =>
        setSearchParams(new URLSearchParams(search), { replace }),
      // React Router re-renders on its own location changes, so no extra subscription is needed.
      subscribe: () => () => undefined,
    }),
    [setSearchParams]
  );
};

export const CardPartnerDirectoryPage: FC = (): JSX.Element => {
  const listScrollContainerRef = useRef<Element | null>(null);
  if (listScrollContainerRef.current === null) {
    listScrollContainerRef.current = document.getElementById(TestIds.pages.mainScrollContainer);
  }

  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const adapter = useReactRouterRoutingAdapter();
  const { categories } = useCategoriesQuery(categoriesApiConfig);
  const { directoryProps: routingDirectoryProps, topBarProps } = useCardPartnersRouting({
    categories,
    adapter,
  });

  const headerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapHeight, setMapHeight] = useState<string>();

  const isMapView = routingDirectoryProps.view === CardPartnersDirectoryView.Map;
  const isPartnerPage = routingDirectoryProps.selectedPartnerId != null;

  useEffect(() => {
    document
      .getElementById(TestIds.pages.mainScrollContainer)
      ?.scrollTo({ top: 0, behavior: "auto" });
  }, [routingDirectoryProps.selectedPartnerId]);

  useEffect(() => {
    if (!isMapView) {
      setMapHeight(undefined);
      return;
    }
    // Size the map to the space between its top edge and the bottom of the page
    // content area. Top is measured from the map container; bottom inset comes
    // from main-scroll-container (bottom nav padding on mobile) plus
    // ResponsivePageBody's own padding-bottom.
    const measure = (): void => {
      const mapContainer = mapContainerRef.current;
      if (!mapContainer) {
        return;
      }

      const scrollContainer = document.getElementById(TestIds.pages.mainScrollContainer);
      if (!scrollContainer) {
        return;
      }

      const mapContainerTop = mapContainer.getBoundingClientRect().top;
      const { bottom } = scrollContainer.getBoundingClientRect();
      const scrollPaddingBottom = Number.parseFloat(
        getComputedStyle(scrollContainer).paddingBottom
      );
      const pageBody = scrollContainer.firstElementChild as HTMLElement | null;
      const pageBodyPaddingBottom = pageBody
        ? Number.parseFloat(getComputedStyle(pageBody).paddingBottom)
        : 0;

      setMapHeight(
        `${Math.max(0, bottom - scrollPaddingBottom - pageBodyPaddingBottom - mapContainerTop)}px`
      );
    };
    measure();

    const observer = new ResizeObserver(measure);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    const scrollContainer = document.getElementById(TestIds.pages.mainScrollContainer);
    if (scrollContainer) {
      observer.observe(scrollContainer);
    }
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isMapView]);

  const directoryProps: CardPartnersDirectoryProps = {
    ...routingDirectoryProps,
    mapConfig,
    branchesApi,
    partnerAssets,
    shareBaseUrl: cardPartnersDirectoryWebUrl,
    topBar: { ...topBarProps },
  };

  const theme = useTheme();
  const isMobile = useIsMobile(theme.breakpoints.values.md);
  const listStickyTopOffset = isMobile
    ? `calc(${APPBAR_HEIGHT.sm}px + var(--app-safe-area-top) + ${MOBILE_LIST_STICKY_TOP_OFFSET})`
    : `calc(var(--header-mountains-height) + ${MOBILE_LIST_STICKY_TOP_OFFSET})`;

  return (
    <PageContainer
      data-testid={TestIds.feat.cardPartnersDirectory.page.container}
      sx={isPartnerPage ? undefined : { maxWidth: "100%" }}
    >
      <div ref={headerRef} className={isPartnerPage ? undefined : "pb-0 md:pb-6"}>
        {!isPartnerPage && (
          <div className="hidden max-w-[50rem] flex-col gap-2 md:flex">
            <h1 className="text-3xl font-extrabold text-forest-green-950">
              Farmlands Card Partners
            </h1>
            <p className="text-sm text-foreground">
              Exclusive offers, discounts and rebates from over 7,000 partner locations across
              Aotearoa
            </p>
          </div>
        )}
      </div>
      <div
        ref={mapContainerRef}
        className="relative z-0 w-full"
        style={isMapView ? { height: mapHeight } : undefined}
      >
        <CardPartnersDirectory
          {...directoryProps}
          listScrollMode={ListScrollMode.Page}
          listScrollContainerRef={listScrollContainerRef}
          listStickyTopOffset={listStickyTopOffset}
        />
      </div>
    </PageContainer>
  );
};
